import { http } from '../shared/api';
import { templateFileName } from '../shared/utils/templateFileName';
import {
  requireImportStatusUrl,
  requireRecalculationStatusUrl,
} from '../shared/api/operationStatusUrl';

const DATA_INPUT_MUTATION_TIMEOUT_MS = 120000;

export const getDataInputOverview = (fiscalYear, config) =>
  http.get(`/api/data-input/${Number(fiscalYear)}/overview`, config);

export const getDataInputImportHistory = (fiscalYear, config) =>
  http.get(`/api/data-input/${Number(fiscalYear)}/import-history`, config);

export const getDataInputDriverHistory = (fiscalYear, config) =>
  http.get(`/api/data-input/${Number(fiscalYear)}/driver-history`, config);

export const getDataRecalculationHistory = (fiscalYear, config) =>
  http.get(`/api/data-input/${Number(fiscalYear)}/recalculations`, config);

export const getDataRecalculationReadiness = (fiscalYear, config) =>
  http.get(`/api/data-input/${Number(fiscalYear)}/recalculations/readiness`, config);

export async function downloadDataInputTemplate(fiscalYear, importType, config) {
  const normalizedImportType = String(importType || '');
  const response = await http.downloadResponse(
    `/api/data-input/${Number(fiscalYear)}/import-templates/${encodeURIComponent(normalizedImportType)}`,
    config,
  );
  return {
    blob: response.data,
    fileName: templateFileName(
      response.headers?.['content-disposition'],
      normalizedImportType,
    ),
  };
}

export function createDataImport({ fiscalYear, importType, file, idempotencyKey }) {
  const form = new FormData();
  form.append('fiscal_year', String(fiscalYear));
  form.append('import_type', importType);
  form.append('file', file);
  return http.upload('/api/data-imports', form, {
    headers: { 'Idempotency-Key': idempotencyKey },
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  });
}

export const getDataImportStatus = (statusUrl, importId, config) => http.get(
  requireImportStatusUrl(statusUrl, importId),
  config,
);

export const cancelDataImport = (importId, config) => http.post(
  `/api/data-imports/${encodeURIComponent(importId)}/cancel`,
  {},
  config,
);

export const createDataImportDownloadLink = (importId, config) => http.post(
  `/api/data-imports/${encodeURIComponent(importId)}/download-link`,
  {},
  config?.fileId
    ? { ...config, params: { ...(config.params || {}), file_id: config.fileId } }
    : config,
);

export const getActiveDataImports = (config) => http.get('/api/data-imports/active', config);

export const syncSeatDriver = (fiscalYear, payload, idempotencyKey) => http.post(
  `/api/data-input/${Number(fiscalYear)}/seat-syncs`,
  {
    as_of_date: payload.asOfDate || undefined,
  },
  {
    headers: { 'Idempotency-Key': idempotencyKey },
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  },
);

export const createSeatManualInput = (fiscalYear, payload, idempotencyKey) => http.post(
  `/api/data-input/${Number(fiscalYear)}/seat-manual-inputs`,
  {
    as_of_date: payload.asOfDate,
    total_seats: payload.totalSeats,
    working_days: payload.workingDays,
  },
  {
    headers: { 'Idempotency-Key': idempotencyKey },
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  },
);

export const syncCat8Driver = (fiscalYear, payload, idempotencyKey) => http.post(
  `/api/data-input/${Number(fiscalYear)}/cat-8-syncs`,
  {
    as_of_date: payload.asOfDate || undefined,
  },
  {
    headers: { 'Idempotency-Key': idempotencyKey },
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  },
);

export const createCat8ManualInput = (fiscalYear, payload, idempotencyKey) => http.post(
  `/api/data-input/${Number(fiscalYear)}/cat-8-manual-inputs`,
  {
    as_of_date: payload.asOfDate,
    total_area_m2: payload.totalAreaM2,
  },
  {
    headers: { 'Idempotency-Key': idempotencyKey },
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  },
);

export const getDriverSnapshot = (snapshotId, config) => http.get(
  `/api/data-input/driver-snapshots/${encodeURIComponent(snapshotId)}`,
  config,
);

export const saveCat19Outsourcing = (fiscalYear, payload) => http.put(
  `/api/data-input/${Number(fiscalYear)}/cat-1-9`,
  payload,
  {
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  },
);

export const runDataRecalculation = (fiscalYear, payload, idempotencyKey) => http.post(
  `/api/data-input/${Number(fiscalYear)}/recalculations`,
  payload,
  {
    headers: { 'Idempotency-Key': idempotencyKey },
  },
);

export const getDataRecalculationStatus = (
  statusUrl,
  fiscalYear,
  operationId,
  config,
) => http.get(
  requireRecalculationStatusUrl(statusUrl, fiscalYear, operationId),
  config,
);

export const deleteDataInput = (fiscalYear, payload) => http.post(
  `/api/data-input/${Number(fiscalYear)}/deletions`,
  payload,
  {
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  },
);

export const getScope3AfSnapshot = (fiscalYear, config) => http.get(
  `/api/data-input/${Number(fiscalYear)}/scope3-af-snapshot`,
  config,
);

export function confirmScope3AfImport({
  fiscalYear,
  expectedSnapshotId,
  retainedFileIds,
  files,
  idempotencyKey,
}) {
  const form = new FormData();
  form.append('expected_snapshot_id', String(expectedSnapshotId || ''));
  (retainedFileIds || []).forEach((fileId) => form.append('retained_file_ids', fileId));
  (files || []).forEach((file) => form.append('files', file));
  return http.upload(`/api/data-input/${Number(fiscalYear)}/scope3-af-imports`, form, {
    headers: { 'Idempotency-Key': idempotencyKey },
    timeout: DATA_INPUT_MUTATION_TIMEOUT_MS,
  });
}
